<template>
  <div class="project-view">
    <!-- Breadcrumb header bar -->
    <div class="project-topbar">
      <button class="back-link" @click="$emit('close-project')">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
        Back
      </button>
    </div>

    <!-- Project content: two-column layout -->
    <div class="project-body">

      <!-- Left column: title + tabs + list -->
      <div class="project-main">
        <div class="project-title-row">
          <div class="project-name-group">
            <h1 class="project-name">{{ project.name }}</h1>
            <div class="help-tooltip-wrapper" ref="helpTooltipWrapper">
              <button
                class="help-tooltip-trigger"
                @click.stop="showHelpTooltip = !showHelpTooltip"
                :class="{ active: showHelpTooltip }"
                title="How projects work"
                aria-label="How projects work"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
              </button>
              <transition name="help-tooltip">
                <div v-if="showHelpTooltip" class="help-tooltip-popover" role="tooltip" @click.stop>
                  <div class="help-tooltip-header">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                    </svg>
                    <span>How to use projects</span>
                  </div>
                  <p class="help-tooltip-body">
                    Projects keep related chats and files together so the AI can reference them across conversations.
                  </p>
                  <ul class="help-tooltip-list">
                    <li><strong>Start a chat</strong> — click <em>New chat</em> above to begin a conversation inside this project.</li>
                    <li><strong>Upload documents</strong> — add PDFs or files under the <em>Documents</em> tab. The AI can cite them in any chat within this project.</li>
                    <li><strong>Browse history</strong> — every chat started here is listed under <em>Your chats</em>, so you can pick up where you left off.</li>
                    <li><strong>Jump back anytime</strong> — click the project name pill at the top of any chat to return to this page.</li>
                  </ul>
                </div>
              </transition>
            </div>
          </div>
          <div class="project-title-actions">
            <button class="share-btn pill-btn pill-btn--secondary" @click="$emit('new-chat-in-project', project)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              New chat
            </button>
          </div>
        </div>

        <p v-if="project.description" class="project-meta-description">
          {{ descriptionIsLong && !descriptionExpanded ? project.description.slice(0, descriptionLimit).trimEnd() + '… ' : project.description }}
          <button
            v-if="descriptionIsLong"
            class="description-toggle"
            @click="descriptionExpanded = !descriptionExpanded"
          >
            {{ descriptionExpanded ? 'View less' : 'View more' }}
          </button>
        </p>

        <!-- Tabs -->
        <div class="project-tabs">
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'conversations' }"
            @click="activeTab = 'conversations'"
          >
            Your chats
            <span class="tab-badge" :class="{ active: activeTab === 'conversations' }">{{ conversations.length }}</span>
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'documents' }"
            @click="activeTab = 'documents'"
          >
            Documents
            <span class="tab-badge" :class="{ active: activeTab === 'documents' }">{{ documents.length }}</span>
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'artifacts' }"
            @click="activeTab = 'artifacts'"
          >
            Artifacts
            <span class="tab-badge" :class="{ active: activeTab === 'artifacts' }">{{ artifacts.length }}</span>
          </button>
        </div>

        <!-- Tab content -->
        <div class="tab-content">
          <!-- Conversations -->
          <div v-if="activeTab === 'conversations'">
            <div v-if="conversations.length === 0" class="empty-state">
              <div class="empty-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.35">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
              </div>
              <p class="empty-title">No chats yet</p>
              <p class="empty-sub">Start a conversation in this project</p>
              <button class="pill-btn pill-btn--primary" @click="$emit('new-chat-in-project', project)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                New chat
              </button>
            </div>
            <div v-else class="chat-list">
              <div
                v-for="conv in conversations"
                :key="conv.id"
                class="chat-row"
                @click="$emit('open-conversation', conv)"
              >
                <div class="chat-row-icon">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                </div>
                <div class="chat-row-content">
                  <span class="chat-row-title">{{ conv.name || 'Untitled Chat' }}</span>
                  <span class="chat-row-meta">{{ formatDate(conv.created_at) }}</span>
                </div>
                <button class="chat-row-delete" @click.stop="deleteConversation(conv)" title="Delete">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Documents -->
          <div v-if="activeTab === 'documents'">
            <!-- Pending files (staged, not yet uploaded) -->
            <div v-if="pendingFiles.length > 0" class="pending-upload">
              <div class="pending-upload-header">
                <span class="pending-upload-title">Ready to upload ({{ pendingFiles.length }})</span>
                <span class="pending-upload-sub">Review your selection, then click Upload.</span>
              </div>
              <div class="pending-file-list">
                <div v-for="(file, index) in pendingFiles" :key="index" class="pending-file-row">
                  <div class="chat-row-icon">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                    </svg>
                  </div>
                  <div class="chat-row-content">
                    <span class="chat-row-title">{{ file.name }}</span>
                    <span class="chat-row-meta">{{ formatFileSize(file.size) }}</span>
                  </div>
                  <button class="chat-row-delete" @click.stop="removePendingFile(index)" title="Remove">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              </div>
              <div class="pending-upload-actions">
                <button class="pill-btn pill-btn--secondary" @click="pickDocuments" :disabled="uploading">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add more
                </button>
                <button class="pill-btn pill-btn--ghost" @click="cancelPendingUploads" :disabled="uploading">
                  Cancel
                </button>
                <button class="pill-btn pill-btn--primary" @click="confirmUpload" :disabled="uploading">
                  <svg v-if="!uploading" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  {{ uploading ? 'Uploading…' : `Upload ${pendingFiles.length} file${pendingFiles.length > 1 ? 's' : ''}` }}
                </button>
              </div>
            </div>

            <div v-if="documents.length === 0 && pendingFiles.length === 0" class="empty-state">
              <div class="empty-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.35">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
              </div>
              <p class="empty-title">No documents uploaded</p>
              <p class="empty-sub">Add PDFs, documents, or files to reference in this project</p>
              <button class="pill-btn pill-btn--primary" @click="pickDocuments">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                Choose file
              </button>
            </div>
            <div v-else-if="documents.length > 0" class="documents-section">
              <div v-if="pendingFiles.length === 0" class="documents-toolbar">
                <button class="pill-btn pill-btn--primary" @click="pickDocuments">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add files
                </button>
              </div>
              <div class="chat-list">
              <div v-for="doc in documents" :key="doc.id" class="chat-row">
                <div class="chat-row-icon">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                  </svg>
                </div>
                <div class="chat-row-content">
                  <span class="chat-row-title">
                    {{ doc.file_name }}
                    <span v-if="doc.parse_status === 'success'" class="parse-badge parse-success" title="Content extracted">
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                    </span>
                    <span v-else-if="doc.parse_status === 'partial'" class="parse-badge parse-partial" title="Partial extraction">
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                    </span>
                    <span v-else-if="doc.parse_status === 'failed'" class="parse-badge parse-failed" title="Extraction failed">
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </span>
                  </span>
                  <span class="chat-row-meta">Uploaded {{ formatDate(doc.uploaded_at) }}</span>
                </div>
                <button class="chat-row-action" @click.stop="downloadDocument(doc)" title="Download">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                </button>
                <button class="chat-row-delete" @click.stop="deleteDocument(doc)" title="Delete">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                </button>
              </div>
              </div>
            </div>
          </div>

          <!-- Artifacts -->
          <div v-if="activeTab === 'artifacts'">
            <div v-if="loadingArtifacts" class="loading-state">
              <div class="loading-spinner"></div>
            </div>
            <div v-else-if="artifacts.length === 0" class="empty-state">
              <div class="empty-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.35">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                </svg>
              </div>
              <p class="empty-title">No artifacts yet</p>
              <p class="empty-sub">Files created by AI during conversations appear here</p>
            </div>
            <div v-else class="chat-list">
              <div v-for="artifact in artifacts" :key="artifact.id" class="chat-row">
                <div class="chat-row-icon">
                  <svg v-if="artifact.file_type && artifact.file_type.startsWith('image/')" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                  <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                  </svg>
                </div>
                <div class="chat-row-content">
                  <span class="chat-row-title">{{ artifact.file_name }}</span>
                  <span class="chat-row-meta">{{ artifact.conversation_name || 'Conversation' }} &bull; {{ formatDate(artifact.created_at) }}</span>
                </div>
                <button class="chat-row-action" @click.stop="downloadArtifact(artifact)" title="Download">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right column: side panels -->
      <div class="project-sidebar" :style="{ width: sidebarWidth + 'px' }">
        <div
          class="resize-handle"
          @mousedown.prevent="startResize"
        ></div>
        <!-- Upload document panel (visible on docs/general) -->
        <div class="side-panel">
          <div class="side-panel-header">
            <span class="side-panel-title">Files</span>
            <button class="side-panel-action" @click="pickDocuments" title="Add file">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
          </div>
          <div v-if="documents.length === 0" class="side-panel-empty">
            <div class="side-panel-empty-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.4">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
            </div>
            <p class="side-panel-empty-text">Add PDFs, documents, or other text to reference in this project</p>
          </div>
          <div v-else class="side-panel-file-list">
            <div v-for="doc in documents" :key="doc.id" class="side-panel-file-row">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="side-panel-file-icon">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
              <span class="side-panel-file-name">{{ doc.file_name }}</span>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Hidden file input -->
    <input ref="fileInput" type="file" multiple style="display: none" @change="handleFileSelect" />
  </div>
</template>

<script>
import axios from '@/axios-config';

export default {
  name: 'ProjectView',
  props: {
    project: {
      type: Object,
      required: true
    },
    allConversations: {
      type: Array,
      default: () => []
    }
  },
  data() {
    const stored = parseInt(localStorage.getItem('camgpt.projectView.sidebarWidth'), 10);
    return {
      documents: [],
      artifacts: [],
      loadingArtifacts: false,
      activeTab: 'conversations',
      sidebarWidth: stored && stored >= 240 && stored <= 600 ? stored : 280,
      pendingFiles: [],
      uploading: false,
      showHelpTooltip: false,
      descriptionExpanded: false,
      descriptionLimit: 180
    };
  },
  computed: {
    conversations() {
      if (!this.allConversations) return [];
      return this.allConversations.filter(conv => conv.project === this.project.id);
    },
    descriptionIsLong() {
      return !!this.project.description && this.project.description.length > this.descriptionLimit;
    }
  },
  mounted() {
    this.loadProjectData();
    document.addEventListener('click', this.onOutsideClickForHelp);
    document.addEventListener('keydown', this.onKeydownForHelp);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onOutsideClickForHelp);
    document.removeEventListener('keydown', this.onKeydownForHelp);
  },
  watch: {
    'project.id'() {
      this.loadProjectData();
      this.descriptionExpanded = false;
    }
  },
  methods: {
    async loadProjectData() {
      try {
        const projectResponse = await axios.get(`/api/projects/${this.project.id}/`);
        this.documents = projectResponse.data.documents || [];
        this.loadArtifacts();
      } catch (error) {
        console.error('Error loading project data:', error);
      }
    },
    async loadArtifacts() {
      this.loadingArtifacts = true;
      try {
        const response = await axios.get(`/api/projects/${this.project.id}/artifacts/`);
        this.artifacts = response.data;
      } catch (error) {
        console.error('Error loading artifacts:', error);
        this.artifacts = [];
      } finally {
        this.loadingArtifacts = false;
      }
    },
    pickDocuments() {
      this.activeTab = 'documents';
      this.$refs.fileInput.click();
    },
    handleFileSelect(event) {
      const files = Array.from(event.target.files || []);
      if (files.length === 0) return;
      this.pendingFiles.push(...files);
      this.activeTab = 'documents';
      event.target.value = '';
    },
    removePendingFile(index) {
      this.pendingFiles.splice(index, 1);
    },
    cancelPendingUploads() {
      this.pendingFiles = [];
    },
    async confirmUpload() {
      if (this.pendingFiles.length === 0 || this.uploading) return;
      this.uploading = true;
      try {
        const res = await axios.get('/api/get-csrf-token');
        const token = res.data.csrfToken;
        for (const file of this.pendingFiles) {
          const formData = new FormData();
          formData.append('file', file);
          await axios.post(`/api/projects/${this.project.id}/documents/`, formData, {
            headers: {
              'X-CSRFToken': token,
              'Content-Type': 'multipart/form-data'
            }
          });
        }
        this.pendingFiles = [];
        await this.loadProjectData();
      } catch (error) {
        console.error('Error uploading document:', error);
        alert('Failed to upload one or more documents');
      } finally {
        this.uploading = false;
      }
    },
    onOutsideClickForHelp(event) {
      if (!this.showHelpTooltip) return;
      const wrapper = this.$refs.helpTooltipWrapper;
      if (wrapper && !wrapper.contains(event.target)) {
        this.showHelpTooltip = false;
      }
    },
    onKeydownForHelp(event) {
      if (event.key === 'Escape' && this.showHelpTooltip) {
        this.showHelpTooltip = false;
      }
    },
    formatFileSize(bytes) {
      if (!bytes && bytes !== 0) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
      return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    },
    deleteConversation(conv) {
      if (!confirm(`Delete "${conv.name || 'Untitled Chat'}"?`)) return;
      this.$emit('delete-conversation', conv.id);
    },
    downloadDocument(doc) {
      window.open(doc.file_path, '_blank');
    },
    async deleteDocument(doc) {
      if (!confirm(`Delete "${doc.file_name}"?`)) return;
      try {
        const res = await axios.get('/api/get-csrf-token');
        const token = res.data.csrfToken;
        await axios.delete(`/api/projects/${this.project.id}/documents/${doc.id}/`, {
          headers: { 'X-CSRFToken': token }
        });
        this.documents = this.documents.filter(d => d.id !== doc.id);
      } catch (error) {
        console.error('Error deleting document:', error);
        alert('Failed to delete document');
      }
    },
    downloadArtifact(artifact) {
      window.open(artifact.file_url, '_blank');
    },
    startResize(event) {
      this.resizeDragStartX = event.clientX;
      this.resizeDragStartWidth = this.sidebarWidth;
      this.boundResizeMove = this.onResizeMove.bind(this);
      this.boundResizeUp = this.onResizeUp.bind(this);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
      window.addEventListener('mousemove', this.boundResizeMove);
      window.addEventListener('mouseup', this.boundResizeUp);
    },
    onResizeMove(event) {
      const delta = this.resizeDragStartX - event.clientX;
      const next = this.resizeDragStartWidth + delta;
      this.sidebarWidth = Math.min(600, Math.max(240, next));
    },
    onResizeUp() {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', this.boundResizeMove);
      window.removeEventListener('mouseup', this.boundResizeUp);
      localStorage.setItem('camgpt.projectView.sidebarWidth', this.sidebarWidth);
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;

      return date.toLocaleDateString();
    }
  }
};
</script>

<style scoped>
.project-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
  background: var(--bg-primary);
  animation: view-enter 0.32s cubic-bezier(0.4, 0, 0.2, 1) both;
}

@keyframes view-enter {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ── Breadcrumb top bar ── */
.project-topbar {
  display: flex;
  align-items: center;
  padding: var(--space-md) var(--space-2xl);
  border-bottom: 1px solid var(--border-subtle);
  flex-shrink: 0;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  background: none;
  border: none;
  cursor: pointer;
  font-size: var(--text-sm);
  color: var(--text-tertiary);
  padding: var(--space-xs) 0;
  transition: color var(--duration-base) var(--ease-default);
  font-family: var(--font-sans);
}

.back-link:hover {
  color: var(--accent-primary);
}

/* ── Two-column body ── */
.project-body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* ── Left main column ── */
.project-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: var(--space-2xl);
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--scrollbar-thumb) var(--scrollbar-track);
}

.project-main::-webkit-scrollbar {
  width: 6px;
}

.project-main::-webkit-scrollbar-thumb {
  background: var(--scrollbar-thumb);
  border-radius: var(--radius-pill);
}

/* ── Project title row ── */
.project-title-row {
  display: flex;
  align-items: center;
  gap: var(--space-md);
  margin-bottom: var(--space-xs);
}

.project-name-group {
  flex: 1;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  min-width: 0;
}

.project-name {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  font-family: var(--font-display, var(--font-sans));
  letter-spacing: -0.02em;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Help tooltip ── */
.help-tooltip-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.help-tooltip-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--text-tertiary);
  border-radius: 50%;
  cursor: pointer;
  transition: background var(--duration-base, 0.15s) var(--ease-default, ease),
              color var(--duration-base, 0.15s) var(--ease-default, ease);
}

.help-tooltip-trigger:hover,
.help-tooltip-trigger.active {
  background: var(--accent-muted);
  color: var(--accent-primary);
}

.help-tooltip-popover {
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  z-index: 100;
  width: 340px;
  max-width: calc(100vw - var(--space-2xl) * 2);
  padding: var(--space-md);
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg, 12px);
  box-shadow: var(--shadow-lg, 0 12px 32px rgba(0, 0, 0, 0.12));
}

.help-tooltip-header {
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  margin-bottom: var(--space-sm);
  color: var(--accent-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-semibold, 600);
}

.help-tooltip-header span {
  color: var(--text-primary);
}

.help-tooltip-body {
  margin: 0 0 var(--space-sm) 0;
  font-size: var(--text-sm);
  color: var(--text-secondary);
  line-height: 1.5;
}

.help-tooltip-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
}

.help-tooltip-list li {
  position: relative;
  padding-left: var(--space-md);
  font-size: var(--text-xs);
  color: var(--text-secondary);
  line-height: 1.5;
}

.help-tooltip-list li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.5em;
  width: 4px;
  height: 4px;
  background: var(--accent-primary);
  border-radius: 50%;
}

.help-tooltip-list li strong {
  color: var(--text-primary);
  font-weight: var(--font-semibold, 600);
}

.help-tooltip-list li em {
  font-style: normal;
  color: var(--text-primary);
  font-weight: var(--font-medium, 500);
}

.help-tooltip-enter-active,
.help-tooltip-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.help-tooltip-enter-from,
.help-tooltip-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.project-title-actions {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  flex-shrink: 0;
}

.icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: var(--radius-md);
  border: 1px solid var(--border-subtle);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  transition: background var(--duration-base) var(--ease-default), color var(--duration-base) var(--ease-default), border-color var(--duration-base) var(--ease-default);
}

.icon-btn:hover {
  background: var(--bg-surface-hover);
  color: var(--text-primary);
  border-color: var(--border-primary);
}

.share-btn {
  height: 30px;
  padding: 0 var(--space-md);
  font-size: var(--text-sm);
}

/* Reuse global pill-btn styles if available, else define locally */
.pill-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-xs);
  border-radius: var(--radius-pill);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  cursor: pointer;
  transition: background var(--duration-base) var(--ease-default), box-shadow var(--duration-base) var(--ease-default);
  white-space: nowrap;
  padding: var(--space-sm) var(--space-lg);
  border: 1px solid transparent;
}

.pill-btn--primary {
  background: var(--accent-primary);
  color: var(--text-on-accent);
  border-color: var(--accent-primary);
}

.pill-btn--primary:hover {
  background: var(--accent-hover);
  box-shadow: var(--shadow-sm);
}

.pill-btn--secondary {
  background: var(--bg-surface);
  color: var(--text-primary);
  border-color: var(--border-subtle);
}

.pill-btn--secondary:hover {
  background: var(--bg-surface-hover);
  border-color: var(--border-primary);
}

.pill-btn--ghost {
  background: transparent;
  color: var(--text-tertiary);
  border-color: transparent;
}

.pill-btn--ghost:hover {
  background: var(--bg-surface-hover);
  color: var(--text-primary);
}

.pill-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── Pending upload staging area ── */
.pending-upload {
  margin-bottom: var(--space-xl);
  padding: var(--space-md) var(--space-md) var(--space-sm);
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg, 12px);
}

.pending-upload-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 var(--space-xs) var(--space-sm);
}

.pending-upload-title {
  font-size: var(--text-sm);
  font-weight: var(--font-semibold, 600);
  color: var(--text-primary);
}

.pending-upload-sub {
  font-size: var(--text-xs);
  color: var(--text-tertiary);
}

.pending-file-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pending-file-row {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--radius-md, 8px);
  color: var(--text-secondary);
}

.pending-file-row:hover {
  background: var(--bg-surface-hover);
}

.pending-upload-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-xs) 0;
  margin-top: var(--space-sm);
  border-top: 1px solid var(--border-subtle);
}

.documents-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: var(--space-sm);
}

.project-meta-description {
  margin: 0 0 var(--space-xl) 0;
  font-size: var(--text-sm);
  color: var(--text-tertiary);
  line-height: 1.5;
  max-width: 75%;
}

.description-toggle {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: var(--accent-primary);
  font-weight: var(--font-medium, 500);
  cursor: pointer;
}

.description-toggle:hover {
  text-decoration: underline;
}

/* ── Tabs ── */
.project-tabs {
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--border-subtle);
  margin-bottom: var(--space-xl);
  flex-shrink: 0;
}

.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--space-sm);
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: var(--space-sm) var(--space-lg);
  padding-bottom: calc(var(--space-sm) + 2px);
  cursor: pointer;
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--text-tertiary);
  font-family: var(--font-sans);
  transition: color var(--duration-base) var(--ease-default), border-color var(--duration-base) var(--ease-default);
  margin-bottom: -1px;
}

.tab-btn:hover {
  color: var(--text-primary);
}

.tab-btn.active {
  color: var(--text-primary);
  border-bottom-color: var(--text-primary);
}

.tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: var(--radius-pill);
  background: var(--bg-surface-raised);
  color: var(--text-muted);
  font-size: 11px;
  font-weight: var(--font-semibold);
  line-height: 1;
  transition: background var(--duration-base) var(--ease-default), color var(--duration-base) var(--ease-default);
}

.tab-badge.active {
  background: var(--bg-selected, rgba(33,158,188,0.1));
  color: var(--accent-primary);
}

/* ── Tab content ── */
.tab-content {
  flex: 1;
}

/* ── Chat list rows ── */
.chat-list {
  display: flex;
  flex-direction: column;
}

.chat-row {
  display: flex;
  align-items: center;
  gap: var(--space-md);
  padding: var(--space-md) var(--space-sm);
  border-bottom: 1px solid var(--border-subtle);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: background var(--duration-base) var(--ease-default);
}

.chat-row:last-child {
  border-bottom: none;
}

.chat-row:hover {
  background: var(--bg-surface-hover);
}

.chat-row:hover .chat-row-delete,
.chat-row:hover .chat-row-action {
  opacity: 1;
}

.chat-row-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  color: var(--text-muted);
}

.chat-row-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.chat-row-title {
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-row-meta {
  font-size: 12px;
  color: var(--text-muted);
}

.chat-row-delete,
.chat-row-action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--radius-sm);
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  flex-shrink: 0;
  opacity: 0;
  transition: background var(--duration-base) var(--ease-default), color var(--duration-base) var(--ease-default), opacity var(--duration-base) var(--ease-default);
}

.chat-row-action:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.chat-row-delete:hover {
  background: #fee2e2;
  color: #dc2626;
}

/* ── Empty states ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-3xl) var(--space-xl);
  text-align: center;
  gap: var(--space-sm);
}

.empty-icon {
  color: var(--text-muted);
  margin-bottom: var(--space-sm);
}

.empty-title {
  margin: 0;
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
}

.empty-sub {
  margin: 0 0 var(--space-md) 0;
  font-size: var(--text-sm);
  color: var(--text-muted);
  max-width: 320px;
  line-height: 1.5;
}

/* ── Loading ── */
.loading-state {
  display: flex;
  justify-content: center;
  padding: var(--space-3xl);
}

.loading-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid var(--border-subtle);
  border-top-color: var(--accent-primary);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ── Right sidebar ── */
.project-sidebar {
  flex-shrink: 0;
  position: relative;
  border-left: 1px solid var(--border-subtle);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--scrollbar-thumb) var(--scrollbar-track);
}

.resize-handle {
  position: absolute;
  top: 0;
  left: -4px;
  width: 8px;
  height: 100%;
  cursor: col-resize;
  z-index: 10;
}

.resize-handle::after {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 1px;
  height: 100%;
  background: var(--border-subtle);
  transition: background var(--duration-base) var(--ease-default), width var(--duration-base) var(--ease-default);
}

.resize-handle:hover::after {
  background: var(--accent-primary);
  width: 2px;
}

.project-sidebar::-webkit-scrollbar {
  width: 6px;
}

.project-sidebar::-webkit-scrollbar-thumb {
  background: var(--scrollbar-thumb);
  border-radius: var(--radius-pill);
}

.side-panel {
  border-bottom: 1px solid var(--border-subtle);
  padding: var(--space-lg) var(--space-xl);
}

.side-panel:last-child {
  border-bottom: none;
}

.side-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-md);
}

.side-panel-title {
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
}

.side-panel-action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: var(--radius-sm);
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  transition: background var(--duration-base) var(--ease-default), color var(--duration-base) var(--ease-default);
}

.side-panel-action:hover {
  background: var(--bg-surface-hover);
  color: var(--text-primary);
}

.side-panel-body {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.side-panel-description {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--text-tertiary);
  line-height: 1.55;
}

.side-panel-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--space-sm);
  padding: var(--space-md) 0;
}

.side-panel-empty-icon {
  color: var(--text-muted);
}

.side-panel-empty-text {
  margin: 0;
  font-size: 12px;
  color: var(--text-muted);
  line-height: 1.5;
}

.side-panel-file-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.side-panel-file-row {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.side-panel-file-icon {
  color: var(--text-muted);
  flex-shrink: 0;
}

.side-panel-file-name {
  font-size: var(--text-sm);
  color: var(--text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Parse status badges ── */
.parse-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  margin-left: var(--space-xs);
  vertical-align: middle;
}

.parse-success {
  background: #dcfce7;
  color: #16a34a;
}

.parse-partial {
  background: #fef9c3;
  color: #ca8a04;
}

.parse-failed {
  background: #fee2e2;
  color: #dc2626;
}
</style>
